import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  createFilterOptions
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useAppSelector } from '../../hooks/redux.hooks';
import { deductionsService } from '../../services/deductions.service';
import employeesService from '../../services/employees.service';
import { branchesService } from '../../services/branches.service';
import { CreateDeductionDto, UpdateDeductionDto, Deduction } from '../../types/deductions.types';
import { Employee } from '../../types/employees.types';
import { Branch } from '../../types/branches.types';

interface DeductionDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  deduction?: Deduction | null;
}

const filterOptions = createFilterOptions<Employee>({
  matchFrom: 'any',
  stringify: (option: Employee) => `${option.employeeCode} ${option.fullName} ${option.fullNameAr}`,
});

const CONFIDENTIAL_TYPES = ['LoanDeduction', 'Insurance', 'SocialSecurity'];

const DeductionDialog: React.FC<DeductionDialogProps> = ({ open, onClose, onSave, deduction }) => {
  const isRTL = localStorage.getItem('language') === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const user = useAppSelector((state) => state.auth.user);
  const permissions = useAppSelector((state) => state.auth.permissions);
  const isSuperAdmin = user?.isSuperAdmin === true;
  const canAccessConfidential = isSuperAdmin || permissions?.includes('DEDUCTION_APPROVE') || permissions?.includes('PAYROLL_CREATE');
  const [branches, setBranches] = useState<Branch[]>([]);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    branchId: 0,
    employeeId: 0,
    deductionType: 'Penalty',
    amount: 0,
    deductionDate: new Date().toISOString().split('T')[0],
    reason: '',
    description: '',
    isRecurring: false,
    totalInstallments: 0,
    referenceNumber: '',
    notes: ''
  });

  const t = {
    title: isRTL ? (deduction ? 'تعديل خصم' : 'إضافة خصم') : (deduction ? 'Edit Deduction' : 'Add Deduction'),
    branch: isRTL ? 'الفرع' : 'Branch',
    employee: isRTL ? 'الموظف' : 'Employee',
    deductionType: isRTL ? 'نوع الخصم' : 'Deduction Type',
    amount: isRTL ? 'المبلغ' : 'Amount',
    deductionDate: isRTL ? 'تاريخ الخصم' : 'Deduction Date',
    reason: isRTL ? 'السبب' : 'Reason',
    description: isRTL ? 'الوصف' : 'Description',
    isRecurring: isRTL ? 'دفعات متكررة' : 'Recurring Installments',
    totalInstallments: isRTL ? 'عدد الدفعات' : 'Total Installments',
    referenceNumber: isRTL ? 'رقم المرجع' : 'Reference Number',
    notes: isRTL ? 'ملاحظات' : 'Notes',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    selectBranch: isRTL ? 'اختر الفرع' : 'Select Branch',
    penalty: isRTL ? 'جزاء' : 'Penalty',
    loan: isRTL ? 'قرض' : 'Loan',
    insurance: isRTL ? 'تأمين' : 'Insurance',
    socialSecurity: isRTL ? 'تأمينات اجتماعية' : 'Social Security',
    advance: isRTL ? 'سلفة' : 'Advance',
    damage: isRTL ? 'تلفيات' : 'Damage',
    other: isRTL ? 'أخرى' : 'Other',
    saveSuccess: isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully'
  };

  useEffect(() => {
    if (open) {
      loadData();
      if (deduction) {
        setFormData({
          branchId: deduction.branchId,
          employeeId: deduction.employeeId,
          deductionType: deduction.deductionType,
          amount: deduction.amount,
          deductionDate: deduction.deductionDate.split('T')[0],
          reason: deduction.reason || '',
          description: deduction.description || '',
          isRecurring: deduction.isRecurring,
          totalInstallments: deduction.totalInstallments || 0,
          referenceNumber: deduction.referenceNumber || '',
          notes: deduction.notes || ''
        });
      } else {
        resetForm();
      }
    }
  }, [open, deduction]);

  useEffect(() => {
    if (formData.branchId) {
      setFilteredEmployees(allEmployees.filter(emp => emp.branchId === formData.branchId));
    } else {
      setFilteredEmployees(allEmployees);
    }
  }, [formData.branchId, allEmployees]);

  const loadData = async () => {
    try {
      const [branchesData, employeesData] = await Promise.all([
        branchesService.getAll(),
        employeesService.getAll()
      ]);
      setBranches(branchesData);
      setAllEmployees(employeesData.data || employeesData);
    } catch (error) {
      toast.error('Error loading data');
    }
  };

  const resetForm = () => {
    setFormData({
      branchId: 0,
      employeeId: 0,
      deductionType: 'Penalty',
      amount: 0,
      deductionDate: new Date().toISOString().split('T')[0],
      reason: '',
      description: '',
      isRecurring: false,
      totalInstallments: 0,
      referenceNumber: '',
      notes: ''
    });
  };

  const handleSave = async () => {
    if (!formData.branchId || !formData.employeeId || !formData.amount || !formData.reason) {
      toast.error('Branch, Employee, Amount, and Reason are required');
      return;
    }

    try {
      setLoading(true);
      if (deduction) {
        await deductionsService.update({ ...formData, deductionId: deduction.deductionId } as UpdateDeductionDto);
      } else {
        await deductionsService.create(formData as CreateDeductionDto);
      }
      toast.success(t.saveSuccess);
      onSave();
      onClose();
    } catch (error) {
      toast.error('Error saving');
    } finally {
      setLoading(false);
    }
  };

  const allDeductionTypes = [
    { value: 'Penalty', label: t.penalty },
    { value: 'LoanDeduction', label: t.loan },
    { value: 'Insurance', label: t.insurance },
    { value: 'SocialSecurity', label: t.socialSecurity },
    { value: 'Advance', label: t.advance },
    { value: 'Damage', label: t.damage },
    { value: 'Other', label: t.other }
  ];
  const deductionTypes = canAccessConfidential
    ? allDeductionTypes
    : allDeductionTypes.filter(dt => !CONFIDENTIAL_TYPES.includes(dt.value));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'warning.dark', color: 'white' }}>
        {t.title}
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
                value={branches.find(b => b.branchId === formData.branchId) || null}
                onChange={(_, newValue) => setFormData({ ...formData, branchId: newValue?.branchId || 0, employeeId: 0 })}
                renderInput={(params) => <TextField {...params} label={t.branch} required />}
              />
            </Box>

            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <Autocomplete
                options={filteredEmployees}
                getOptionLabel={(option) => `${option.employeeCode} - ${isRTL ? option.fullNameAr : option.fullName}`}
                value={filteredEmployees.find(e => e.employeeId === formData.employeeId) || null}
                onChange={(_, newValue) => setFormData({ ...formData, employeeId: newValue?.employeeId || 0 })}
                renderInput={(params) => <TextField {...params} label={t.employee} required />}
                filterOptions={filterOptions}
                disabled={!formData.branchId}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <TextField select fullWidth label={t.deductionType} value={formData.deductionType} onChange={(e) => setFormData({ ...formData, deductionType: e.target.value })} required>
                {deductionTypes.map(type => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
              </TextField>
            </Box>

            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <TextField fullWidth type="number" label={t.amount} value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0, step: 0.001 }} required />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.deductionDate}
                  value={formData.deductionDate ? new Date(formData.deductionDate) : null}
                  onChange={(date) => setFormData({ ...formData, deductionDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                  format="dd/MM/yyyy"
                  minDate={minDate}
                  maxDate={maxDate}
                  views={['year', 'month', 'day']}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <TextField fullWidth label={t.referenceNumber} value={formData.referenceNumber} onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })} />
            </Box>
          </Box>

          <Box>
            <TextField fullWidth label={t.reason} value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} required />
          </Box>

          <Box>
            <TextField fullWidth label={t.description} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2} />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
              <FormControlLabel
                control={<Checkbox checked={formData.isRecurring} onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked, totalInstallments: e.target.checked ? 1 : 0 })} />}
                label={t.isRecurring}
              />
            </Box>

            {formData.isRecurring && (
              <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                <TextField fullWidth type="number" label={t.totalInstallments} value={formData.totalInstallments} onChange={(e) => setFormData({ ...formData, totalInstallments: parseInt(e.target.value) || 0 })} inputProps={{ min: 1 }} />
              </Box>
            )}
          </Box>

          <Box>
            <TextField fullWidth label={t.notes} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} multiline rows={2} />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">{t.cancel}</Button>
        <Button onClick={handleSave} variant="contained" color="warning" startIcon={<SaveIcon />} disabled={loading}>{t.save}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeductionDialog;